import SquawkConnectionTypeActions from "@/layout-components/squawk-player/SquawkConnectionTypeActions";
import { useStateWithOptionalPersistence } from "@/hooks/useStateWithOptionalPersistence";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";
import { useGetSquawkPlayerV2 } from "@/layout-components/squawk-player/useGetSquawkPlayerV2";
import { useSetSquawkPlayerV2 } from "@/layout-components/squawk-player/useSetSquawkPlayerV2";
import { Switch } from "@headlessui/react";
import { textToSpeech } from "@/utils/tts-utils";

function SoundTestButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4"
      >
        <path d="M10 3.75a.75.75 0 0 0-1.264-.546L4.703 7H3.167a.75.75 0 0 0-.7.48A6.985 6.985 0 0 0 2 10c0 .887.165 1.737.468 2.52.111.29.39.48.7.48h1.535l4.033 3.796A.75.75 0 0 0 10 16.25V3.75ZM15.95 5.05a.75.75 0 0 0-1.06 1.061 5.5 5.5 0 0 1 0 7.778.75.75 0 0 0 1.06 1.06 7 7 0 0 0 0-9.899Z" />
        <path d="M13.829 7.172a.75.75 0 0 0-1.061 1.06 2.5 2.5 0 0 1 0 3.536.75.75 0 0 0 1.06 1.06 4 4 0 0 0 0-5.656Z" />
      </svg>
      Test
    </button>
  );
}

export default function SquawkConfig() {
  const [audioAnnouncementsEnabled, setAudioAnnouncementsEnabled] =
    useStateWithOptionalPersistence("squawk.audioAnnouncements", true);

  const isInternalUser = useHasInternalNewsquawkUserRole();
  const { data: v2Enabled = false } = useGetSquawkPlayerV2();
  const { mutate: setV2Enabled } = useSetSquawkPlayerV2();

  return (
    <div className="divide-y divide-white/10">
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-white">
            Squawk Player Connection Type
          </h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Choose how the audio squawk connects. We recommend the default
            setting for the best experience.
          </p>
        </div>

        <div className="md:col-span-2">
          <SquawkConnectionTypeActions />
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-white">
            Audio Announcements
          </h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Enable or disable voice announcements when connecting to or disconnecting from channels.
            When enabled, you&apos;ll hear audio prompts like &ldquo;Connected to [channel name]&rdquo; and error notifications.
          </p>
        </div>
        <div className="self-center flex items-center gap-4">
          <Switch
            as="div"
            checked={audioAnnouncementsEnabled}
            onChange={setAudioAnnouncementsEnabled}
            className="group relative inline-flex w-12 p-1 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue focus-visible:ring-offset-2 data-[checked]:bg-squawk-blue"
          >
            <span className="sr-only">Enable audio announcements</span>
            <span
              aria-hidden="true"
              className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
            />
          </Switch>
          <SoundTestButton onClick={() => textToSpeech({ text: "Connected to Newsquawk" })} />
        </div>
      </div>

      {isInternalUser && (
        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base/7 font-semibold text-white">
              Experimental Squawk Player v2
            </h2>
            <p className="mt-1 text-sm/6 text-gray-400">
              Try the next generation of the Newsquawk audio player.
              This is an early preview — please report any issues.
            </p>
          </div>
          <div className="self-center flex items-center gap-4">
            <Switch
              as="div"
              checked={v2Enabled}
              onChange={(checked: boolean) => setV2Enabled(checked)}
              className="group relative inline-flex w-12 p-1 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue focus-visible:ring-offset-2 data-[checked]:bg-squawk-blue"
            >
              <span className="sr-only">Enable experimental squawk player v2</span>
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
              />
            </Switch>
          </div>
        </div>
      )}
    </div>
  );
}
